import { useMemo } from "react";
import { COUNTRY_NAMES, countryToFlag, isoToCountryName } from "@/lib/countryFlags";
import { useOnsiteLocations } from "@/hooks/useOnsiteLocations";

const OTHER_CITY = "__other__";

/** Shared control styling for rounded-xl form fields (notify form, profile edit). */
export const inputClass =
  "w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground text-sm placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/60 transition-all";

// Best-guess country from the browser locale's region (en-GB → United Kingdom).
// Locale region is explicit, unlike timezone which is one-to-many in Europe.
function guessCountryFromLocale(): string {
  const tags = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const tag of tags) {
    const region = new Intl.Locale(tag).region;
    const name = region ? isoToCountryName(region) : "";
    if (name) return name;
  }
  return "";
}

interface Props {
  city: string | null;
  country: string | null;
  onChange: (next: { city: string | null; country: string | null }) => void;
  /** Pre-fill country from browser locale when "isn't listed" is picked. */
  guessCountry?: boolean;
}

/**
 * City/country picker shared by the landing notify form and the profile edit
 * form. Pick a known onsite Quizrun city (which infers its country) or
 * "My city isn't listed" → free-text city + country dropdown. The city list
 * comes from v_onsite_city (onsite, event-backed) so it stays in sync with
 * real events.
 */
export function LocationPicker({ city, country, onChange, guessCountry = false }: Props) {
  const { cities } = useOnsiteLocations();

  // A saved city is "known" only when it exactly matches an onsite city whose
  // country also matches — otherwise it's custom/imported/empty → "isn't listed".
  const isKnown = useMemo(
    () => cities.some((c) => c.city === city && c.country === country),
    [cities, city, country],
  );

  // "Isn't listed" is the default + leads the list; it also covers the unset
  // state (empty free-text city/country = not set). Known options are keyed by
  // city|country so same-named cities in different countries stay distinct.
  const choice = isKnown && city ? `${city}|${country ?? ""}` : OTHER_CITY;
  const isOther = choice === OTHER_CITY;

  const handleChoice = (next: string) => {
    if (next === OTHER_CITY) {
      // Leaving a known city → clear to unset (optionally locale-guess country
      // as a convenience). Already-custom → keep as-is.
      if (isKnown) {
        onChange({ city: null, country: guessCountry ? guessCountryFromLocale() || null : null });
      } else {
        onChange({ city, country });
      }
    } else {
      const [nextCity, nextCountry] = next.split("|");
      onChange({ city: nextCity, country: nextCountry || null });
    }
  };

  return (
    <>
      <select
        aria-label="City"
        value={choice}
        onChange={(e) => handleChoice(e.target.value)}
        className={inputClass}
      >
        <option value={OTHER_CITY}>My city isn't listed</option>
        {cities.map((c) => (
          <option key={`${c.city}|${c.country}`} value={`${c.city}|${c.country}`}>
            {c.city}
          </option>
        ))}
      </select>

      {!isOther && country && (
        <p className="text-xs text-muted-foreground -mt-1 px-1">
          Country: {country} {countryToFlag(country)}
        </p>
      )}

      {isOther && (
        <div className="grid grid-cols-2 gap-3">
          <input
            type="text"
            placeholder="Your city"
            aria-label="Your city"
            value={city ?? ""}
            onChange={(e) => onChange({ city: e.target.value || null, country })}
            className={inputClass}
          />
          <select
            aria-label="Country"
            value={country ?? ""}
            onChange={(e) => onChange({ city, country: e.target.value || null })}
            className={inputClass}
          >
            <option value="">Country</option>
            {COUNTRY_NAMES.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>
      )}
    </>
  );
}
